/**
 * AI Observability Service — the Overview, the calls list and a call's detail,
 * the grounding check, and the Observability Master's settings.
 */

import { api } from './api';
import type {
  AiObservabilityParams,
  ObsGrounding,
  ObsOverview,
  ObsSettings,
  ObsSettingsResponse,
  ObsTraceDetail,
  ObsTraceList,
} from '../types/ai-observability.types';

const BASE_PATH = '/api/v1/ai-observability';

interface ApiEnvelope<T> {
  data: T;
  message?: string;
}

export const aiObservabilityService = {
  /** How the AI did: what broke, where it went off, what needs attention. */
  getOverview: async (params?: AiObservabilityParams): Promise<ObsOverview> => {
    const r = await api.get<ApiEnvelope<ObsOverview>>(`${BASE_PATH}/overview`, { params });
    return r.data.data;
  },

  /** Every traceable call, where it went and where it stopped. */
  getTracing: async (
    params?: AiObservabilityParams & { kind?: string; status?: string;
      failedStep?: string; search?: string; limit?: number;
      promptId?: string; conversationId?: string; ragCode?: string; agentCode?: string },
  ): Promise<ObsTraceList> => {
    const r = await api.get<ApiEnvelope<ObsTraceList>>(`${BASE_PATH}/tracing`, { params });
    return r.data.data;
  },

  /** The Observability Master: settings, defaults and current volume. */
  getSettings: async (): Promise<ObsSettingsResponse> => {
    const r = await api.get<ApiEnvelope<ObsSettingsResponse>>(`${BASE_PATH}/settings`);
    return r.data.data;
  },

  /** Save the Observability Master. Returns the effective settings after save. */
  saveSettings: async (settings: ObsSettings): Promise<ObsSettingsResponse> => {
    const r = await api.put<ApiEnvelope<ObsSettingsResponse>>(`${BASE_PATH}/settings`, settings);
    return r.data.data;
  },

  /** One call in full: its waterfall, knowledge base and source. */
  getTracingDetail: async (observabilityId: string): Promise<ObsTraceDetail> => {
    const r = await api.get<ApiEnvelope<ObsTraceDetail>>(`${BASE_PATH}/tracing/${observabilityId}`);
    return r.data.data;
  },

  /** Ask a judge model how much of the reasoning and answer the context supports. */
  checkGrounding: async (observabilityId: string): Promise<ObsGrounding> => {
    const r = await api.post<ApiEnvelope<ObsGrounding>>(`${BASE_PATH}/tracing/${observabilityId}/grounding`, {});
    return r.data.data;
  },
};
